import { Anchor, Box, Button, Center, Container, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';

import { Logo } from '../components/logo';
import { isEmailAddress } from '../lib/email';
import { TopLoadBar } from '../components/top-load-bar';

// A new password, asked for by email. For anyone with a password account:
// an operator, who signs in on a device and has no other page here, as
// much as an administrator. The answer is the same whether or not the
// address is known, so the page says nothing about who has an account.
export const Route = createFileRoute('/forgot-password')({
  component: ForgotPassword,
});

const NOT_THROUGH = 'The request did not go through. Try again in a moment.';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!isEmailAddress(value)) { setError('Enter the email address of your MotionSafe account.'); return; }
    setError(''); setBusy(true);
    try {
      const res = await fetch('/auth/forgot-password', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: value }) });
      if (res.status === 429) { setError('Too many reset links asked for. Wait 15 minutes, then try again.'); return; }
      // Only an answer that went through says a link may be on its way.
      if (!res.ok) { setError(NOT_THROUGH); return; }
      setSentTo(value);
    } catch {
      setError(NOT_THROUGH);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box component="main" mih="100dvh" style={{ display: 'grid', gridTemplateRows: '1fr auto' }}>
      <Center p="md">
        <Container size={420} w="100%" p={0}>
          <Stack gap="xs" align="center">
            <Logo height={112} mb={24} />
            <Title order={1} ta="center">Reset your password</Title>
            <Text c="dimmed" size="sm" ta="center">For your MotionSafe account, on devices and in the console alike.</Text>
          </Stack>
          <Paper withBorder p={22} mt={30} radius="md" pos="relative">
            {/* Submitting says so along the card's top edge, as the tablet's sign-in card does. */}
            <TopLoadBar active={busy} />
            {sentTo ? (
              <Stack gap="lg">
                <Text>If an account uses <Text span fw={600}>{sentTo}</Text>, a reset link is on its way there. It is good for a short while; open it on any device with a browser.</Text>
                <Text size="sm" c="dimmed">Nothing arrived? Check the spam folder, or ask your organization administrator to send one from the console.</Text>
                <Anchor href="/" size="sm">Back to sign-in</Anchor>
              </Stack>
            ) : (
              <form onSubmit={(e) => void submit(e)} noValidate>
                <Stack gap="lg">
                  <TextInput autoFocus label="Email" type="email" name="email" autoComplete="username" inputMode="email" size="md"
                    value={email} onChange={(ev) => setEmail(ev.currentTarget.value)} error={error || null} placeholder="you@agency.gov" />
                  <Button type="submit" fullWidth disabled={busy}>Email me a reset link</Button>
                  <Text size="sm" c="dimmed">Organizations on single sign-on have no password here; sign in through your identity provider instead.</Text>
                  <Anchor href="/" size="sm">Back to sign-in</Anchor>
                </Stack>
              </form>
            )}
          </Paper>
        </Container>
      </Center>
    </Box>
  );
}
